// Contrail V3.4.2 — dependable drag handles for map editors (zone corners, demo-route points).
//
// Why not MapLibre's own `draggable` markers: they move only while the map is emitting "mousemove" events, and the
// map stops emitting those whenever any of its own gestures is active (pan, zoom inertia, a camera ease from a live
// vehicle update…). In the field that froze a dragged corner after a few pixels and the user had to grab it again.
// Here the handle captures the pointer itself (setPointerCapture), so every move goes to the handle until release,
// whatever the map is doing. Works for mouse, pen and touch.
import type { Map as MLMap, Marker, LngLat as MLLngLat } from "maplibre-gl";

export type DragOpts = {
  onStart?: () => void;
  onMove: (ll: MLLngLat) => void;
  onEnd: (ll: MLLngLat) => void;
  onClick?: () => void;
};

export function dragHandle(map: MLMap, marker: Marker, opts: DragOpts) {
  const el = marker.getElement();
  el.style.touchAction = "none";
  let pid: number | null = null, dragging = false, sx = 0, sy = 0, offX = 0, offY = 0;

  const toLngLat = (e: PointerEvent) => {
    const r = map.getContainer().getBoundingClientRect();
    return map.unproject([e.clientX - r.left - offX, e.clientY - r.top - offY]);
  };
  const down = (e: PointerEvent) => {
    if (e.button !== 0 || pid != null) return;
    e.preventDefault(); e.stopPropagation();          // no map pan, no text selection, no compat mouse events
    const r = map.getContainer().getBoundingClientRect();
    const p = map.project(marker.getLngLat());
    offX = e.clientX - r.left - p.x; offY = e.clientY - r.top - p.y;   // keep the grab point under the pointer
    sx = e.clientX; sy = e.clientY; dragging = false; pid = e.pointerId;
    try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const move = (e: PointerEvent) => {
    if (e.pointerId !== pid) return;
    e.preventDefault(); e.stopPropagation();
    if (!dragging) {
      if (Math.hypot(e.clientX - sx, e.clientY - sy) < 3) return;   // a click, not a drag (yet)
      dragging = true; opts.onStart?.();
    }
    const ll = toLngLat(e);
    marker.setLngLat(ll);
    opts.onMove(ll);
  };
  const finish = (e: PointerEvent, cancelled: boolean) => {
    if (e.pointerId !== pid) return;
    e.stopPropagation();
    const was = dragging;
    pid = null; dragging = false;
    try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (was) opts.onEnd(marker.getLngLat());
    else if (!cancelled) opts.onClick?.();
  };
  const up = (e: PointerEvent) => finish(e, false);
  const cancel = (e: PointerEvent) => finish(e, true);
  const stop = (e: Event) => e.stopPropagation();     // keep clicks/touches on a handle away from the map's own handlers

  el.addEventListener("pointerdown", down);
  el.addEventListener("pointermove", move);
  el.addEventListener("pointerup", up);
  el.addEventListener("pointercancel", cancel);
  el.addEventListener("lostpointercapture", cancel as any);
  for (const t of ["click", "dblclick", "mousedown", "touchstart", "touchmove", "touchend", "contextmenu"]) el.addEventListener(t, stop);
  return {
    isDragging: () => pid != null,
  };
}
